import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { SingleSelectAsync, SelectOption } from './SIngleSelectAsync';
import { prodApiEndpointUrl, stagingApiEndpointUrl } from 'types/apiEndpoints';
import { Tabulator, PageModule, DownloadModule, ExportModule, SortModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import * as d3 from "d3";
import Loader from './Loader';

Tabulator.registerModule([PageModule, DownloadModule, ExportModule, SortModule]);

interface FiscalDistress {
  sf_name: string;
  govt_lvl: string;
  year_rank: number;
  year_cnt: number;
  ugf_balance_coverage_above_status_cnt: number;
  ugf_balance_coverage_score: string;
  gov_fund_balance_coverage_above_status_cnt: number;
  gov_fund_balance_coverage_score: string;
  gov_fund_revenue_coverage_above_status_cnt: number;
  gov_fund_revenue_coverage_score: string;
  cash_coverage_of_gov_fund_expenditure_above_status_cnt: number;
  cash_coverage_of_gov_fund_expenditure_score: string;
  debt_service_as_perc_of_gov_fund_below_status_cnt: number;
  debt_service_as_perc_of_gov_fund_score: string;
  accumulated_depreciation_of_depreciable_capital_assets_status_cnt: number;
  accumulated_depreciation_of_depreciable_capital_assets_score: string;
}

interface FhiData {
  sf_name: string;
  govt_lvl: string;
  report_year: string;
  year_rank: number;
  year_cnt: number;
  ugf_balance_coverage: number;
  ugf_balance_coverage_status: string;
  ugf_balance_coverage_lag: number;
  ugf_balance_coverage_change: string;
  gov_fund_balance_coverage: number;
  gov_fund_balance_coverage_status: string;
  gov_fund_balance_coverage_lag: number;
  gov_fund_balance_coverage_change: string;
  gov_fund_revenue_coverage: number;
  gov_fund_revenue_coverage_status: string;
  gov_fund_revenue_coverage_lag: number;
  gov_fund_revenue_coverage_change: string;
  cash_coverage_of_gov_fund_expenditure: number;
  cash_coverage_of_gov_fund_expenditure_status: string;
  cash_coverage_of_gov_fund_expenditure_lag: number;
  cash_coverage_of_gov_fund_expenditure_change: string;
  debt_service_as_perc_of_gov_fund: number;
  debt_service_as_perc_of_gov_fund_status: string;
  debt_service_as_perc_of_gov_fund_lag: number;
  debt_service_as_perc_of_gov_fund_change: string;
  accumulated_depreciation_of_depreciable_capital_assets: number;
  accumulated_depreciation_of_depreciable_capital_assets_status: string;
  accumulated_depreciation_of_depreciable_capital_assets_lag: number;
  accumulated_depreciation_of_depreciable_capital_assets_change: string;
}

interface UgfBalance {
  report_year: number;
  ugf_balance_coverage: number;
  ugf_balance_coverage_status: string;
  ugf_balance_coverage_lag: number;
  ugf_balance_coverage_change: string;
}

interface GovFundBalance {
  report_year: number;
  gov_fund_balance_coverage: number;
  gov_fund_coverage_status: string;
  gov_fund_coverage_lag: number;
  gov_fund_coverage_change: string;
}

interface GovFundRevenue {
  report_year: number;
  gov_fund_revenue_coverage: number;
  gov_fund_revenue_coverage_status: string;
  gov_fund_revenue_coverage_lag: number;
  gov_fund_revenue_coverage_change: string;
}

interface CashCoverageOfGovFundExpenditure {
  report_year: number;
  cash_coverage_of_gov_fund_expenditure: number;
  cash_coverage_of_gov_fund_expenditure_status: string;
  cash_coverage_of_gov_fund_expenditure_lag: number;
  cash_coverage_of_gov_fund_expenditure_change: string;
}

interface DebtService {
  report_year: number;
  debt_service_as_perc_of_gov_fund: number;
  debt_service_as_perc_of_gov_fund_status: string;
  debt_service_as_perc_of_gov_fund_lag: number;
  debt_service_as_perc_of_gov_fund_change: string;
}

interface AccumulatedDepreciation {
  report_year: number;
  accumulated_depreciation_of_depreciable_capital_assets: number;
  accumulated_depreciation_of_depreciable_capital_assets_status: string;
  accumulated_depreciation_of_depreciable_capital_assets_lag: number;
  accumulated_depreciation_of_depreciable_capital_assets_change: string;
}

interface EntityFiscalDistressIndividualReportsProps {
  allEntities: string[];
  selectedEntityName: string;
  masterData: FiscalDistress[];
}

const EntityFiscalDistressIndividualReports: React.FC<EntityFiscalDistressIndividualReportsProps> = () => {
  const [selectedEntityName, setSelectedEntityName] = useState<string | null>(null);
  const [fhiData, setFhiData] = useState<FhiData[]>([]);
  const [ugfBalance, setUgfBalance] = useState<UgfBalance[]>([]);
  const [govFundBalance, setGovFundBalance] = useState<GovFundBalance[]>([]);
  const [govFundRevenue, setGovFundRevenue] = useState<GovFundRevenue[]>([]);
  const [cashCoverageOfGovFundExpenditure, setCashCoverageOfGovFundExpenditure] = useState<CashCoverageOfGovFundExpenditure[]>([]);
  const [debtService, setDebtService] = useState<DebtService[]>([]);
  const [accumulatedDepreciation, setAccumulatedDepreciation] = useState<AccumulatedDepreciation[]>([]);
  const [allEntities, setAllEntities] = useState<string[]>([]);
  const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  });
  const ugfBalanceTableRef = useRef<HTMLDivElement>(null);
  const govFundBalanceTableRef = useRef<HTMLDivElement>(null);
  const govFundRevenueTableRef = useRef<HTMLDivElement>(null);
  const cashCoverageOfGovFundExpenditureableRef = useRef<HTMLDivElement>(null);
  const debtServiceTableRef = useRef<HTMLDivElement>(null);
  const accumulatedDepreciationTableRef = useRef<HTMLDivElement>(null);
  const ugfBalanceGaugeRef = useRef<HTMLDivElement>(null);
  const govFundBalanceGaugeRef = useRef<HTMLDivElement>(null);
  const govFundRevenueGaugeRef = useRef<HTMLDivElement>(null);
  const cashCoverageGaugeRef = useRef<HTMLDivElement>(null);
  const accumulatedDepreciationGaugeRef = useRef<HTMLDivElement>(null);
  const debtServiceGaugeRef = useRef<HTMLDivElement>(null);
  const [ugfBalanceFDS, setUgfBalanceFDS] = useState<string>("");
  const [govFundBalanceFDS, setGovFundBalanceFDS] = useState<string>("");
  const [govFundRevenueFDS, setGovFundRevenueFDS] = useState<string>("");
  const [cashCoverageFDS, setCashCoverageFDS] = useState<string>("");
  const [debtServiceFDS, setDebtServiceFDS] = useState<string>("");
  const [accumulatedDepreciationFDS, setAccumulatedDepreciationFDS] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const buildApiUrl = (apiEndpointUrl: string, searchParameter: string) => {
    return `${apiEndpointUrl}${searchParameter}`;
  };

  const memoizedEntityNameOptions = useMemo(() => 
    (allEntities || []).map((entity: any) => ({ 
      value: entity.toString(), 
      label: entity.toString(), 
    })), 
    [allEntities]
  );

  const loadEntityOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    if (!inputValue) return memoizedEntityNameOptions;

    const search = inputValue.toLowerCase();
    return memoizedEntityNameOptions.filter((option: any) =>
      option.label.toLowerCase().includes(search)
    );
  }, [memoizedEntityNameOptions]);

  const toTitleCase = (string: string) => {
    if (string.length === 0) return;
    return string.toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
  };

  const getAllEntities = async () => {
    setLoading(true);
    try {
      const getAllEntitiesSearchString = `?function=getFHIScoresNew&parameter=%7B%7D`;
      const res = await fetch(buildApiUrl(apiEndpoint, getAllEntitiesSearchString));
      const unfilteredData = res.ok ? await res.json(): [];
      const entities = unfilteredData.map((item: any) => item.sf_name);

      setAllEntities(entities);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  const getEntityFhiData = async () => {
    if (!selectedEntityName) return;
    setLoading(true);

    try {
      const payload = {
        sf_name: selectedEntityName
      }
      const entityFHIEncodedParams = encodeURIComponent(JSON.stringify(payload));
      const entityFHISearchString = `?function=getSpecificFHIData&parameter=${entityFHIEncodedParams}`;

      const res = await fetch(buildApiUrl(apiEndpoint, entityFHISearchString));
      const entityFhiData: FhiData[] = await res.json();
      setFhiData(entityFhiData);

      const ugfBalanceData: UgfBalance[] = entityFhiData.map((item) => ({
        report_year: Number(item.report_year),
        ugf_balance_coverage: item.ugf_balance_coverage,
        ugf_balance_coverage_status: item.ugf_balance_coverage_status,
        ugf_balance_coverage_lag: item.ugf_balance_coverage_lag,
        ugf_balance_coverage_change: item.ugf_balance_coverage_change,
      }));
      setUgfBalance(ugfBalanceData);

      const govFundBalanceData: GovFundBalance[] = entityFhiData.map((item) => ({
        report_year: Number(item.report_year),
        gov_fund_balance_coverage: item.gov_fund_balance_coverage,
        gov_fund_coverage_status: item.gov_fund_balance_coverage_status,
        gov_fund_coverage_lag: item.gov_fund_balance_coverage_lag,
        gov_fund_coverage_change: item.gov_fund_balance_coverage_change,
      }));
      setGovFundBalance(govFundBalanceData);

      const govFundRevenueData: GovFundRevenue[] = entityFhiData.map((item) => ({
        report_year: Number(item.report_year),
        gov_fund_revenue_coverage: item.gov_fund_revenue_coverage,
        gov_fund_revenue_coverage_status: item.gov_fund_revenue_coverage_status,
        gov_fund_revenue_coverage_lag: item.gov_fund_revenue_coverage_lag,
        gov_fund_revenue_coverage_change: item.gov_fund_revenue_coverage_change,
      }));
      setGovFundRevenue(govFundRevenueData);

      const cashCoverageOfGovFundExpenditureData: CashCoverageOfGovFundExpenditure[] = entityFhiData.map((item) => ({
        report_year: Number(item.report_year),
        cash_coverage_of_gov_fund_expenditure: item.cash_coverage_of_gov_fund_expenditure,
        cash_coverage_of_gov_fund_expenditure_status: item.cash_coverage_of_gov_fund_expenditure_status,
        cash_coverage_of_gov_fund_expenditure_lag: item.cash_coverage_of_gov_fund_expenditure_lag,
        cash_coverage_of_gov_fund_expenditure_change: item.cash_coverage_of_gov_fund_expenditure_change
      }));
      setCashCoverageOfGovFundExpenditure(cashCoverageOfGovFundExpenditureData);

      const debtServiceData: DebtService[] = entityFhiData.map((item) => ({
        report_year: Number(item.report_year),
        debt_service_as_perc_of_gov_fund: item.debt_service_as_perc_of_gov_fund,
        debt_service_as_perc_of_gov_fund_status: item.debt_service_as_perc_of_gov_fund_status,
        debt_service_as_perc_of_gov_fund_lag: item.debt_service_as_perc_of_gov_fund_lag,
        debt_service_as_perc_of_gov_fund_change: item.debt_service_as_perc_of_gov_fund_change
      }));
      setDebtService(debtServiceData);

      const accumulatedDepreciationData: AccumulatedDepreciation[] = entityFhiData.map((item) => ({
        report_year: Number(item.report_year),
        accumulated_depreciation_of_depreciable_capital_assets: item.accumulated_depreciation_of_depreciable_capital_assets,
        accumulated_depreciation_of_depreciable_capital_assets_status: item.accumulated_depreciation_of_depreciable_capital_assets_status,
        accumulated_depreciation_of_depreciable_capital_assets_lag: item.accumulated_depreciation_of_depreciable_capital_assets_lag,
        accumulated_depreciation_of_depreciable_capital_assets_change: item.accumulated_depreciation_of_depreciable_capital_assets_change,
      }));
      setAccumulatedDepreciation(accumulatedDepreciationData);

    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }

  const getFiscalDistressScore = (
    data: any[],
    status: string,
  ) => {
    let fiscalDistressScore: string = "";
    const aboveCount = data.filter((item: any) => item[status] === "above threshold").length;

    fiscalDistressScore = (aboveCount === 3 || aboveCount === 2)
      ? "Satisfactory" 
      : aboveCount === 1 ? "Cautionary" 
      : "Critical";

    return fiscalDistressScore;
  } 

  const createTable = (
    data: any[], 
    targetElement: HTMLDivElement | null,
    yearField: string,
    coverageField: string,
    statusField: string,
    lagField: string,
    changeField: string
  ) => {
    if (!targetElement || !Array.isArray(data)) return null;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;

    return new Tabulator(targetElement, {
      data: data,
      minHeight: "300px",
      maxHeight: "100%",
      layout: isMobile ? "fitDataFill" : "fitColumns",
      pagination: "local",
      paginationSize: 10,
      placeholder: "No Data Available",
      headerSort: true,
      initialSort: [{ column: yearField, dir: "asc" }],
      columns: [
        { 
          title: "Report Year", 
          field: yearField 
        },
        { 
          title: data === ugfBalance ? "UGF Balance Coverage" : data === govFundBalance ? "Gov Fund Balance Coverage" : data === govFundRevenue ? "Gov Fund Revenue Coverage" : data === cashCoverageOfGovFundExpenditure ? "Cash Coverage" : data === debtService ? "Debt Service %" : data === accumulatedDepreciation ? "Asset Coverage" : "",
          field: coverageField, 
          sorter: "number",
          headerSort: true,
          formatter: (cell: any) => {
            const value = cell.getValue();
            if (value === undefined || value === null || isNaN(value)) return "";
          
            const percentage = (Number(value) * 100).toFixed(2);
            
            return `${percentage}%`;
          }
        },
        { 
          title: "Status", 
          field: statusField,
          formatter: (cell: any) => {
            if (cell.getValue() === "above threshold") {
              return `<span class='btn btn-sm btn-success fw-bold'>Above Threshold</span>`;
            }
            else if (cell.getValue() === "below threshold") {
              return `<span class='btn btn-sm btn-danger fw-bold'>Below Threshold</span>`;
            }
            else if (cell.getValue() === "cautionary threshold") {
              return `<span class='btn btn-sm btn-warning fw-bold'>Cautionary Threshold</span>`;
            }
          } 
        },
        { 
          title: "Previous Year Coverage", 
          field: lagField, 
          sorter: "number",
          headerSort: true,
          formatter: (cell: any) => {
            const value = cell.getValue();
            if (value === undefined || value === null || isNaN(value)) return "";
        
            const percentage = (Number(value) * 100).toFixed(2);
            
            return `${percentage}%`;
          }
        },
        { 
          title: "Net Change?", 
          field: changeField, 
          headerSort: true,
          formatter: (cell: any) => {
            const value = cell.getValue();
            if (!value || typeof value !== "string") return "";
            
            return toTitleCase(value);
          }
        }
      ]
    });
  };
  
  // Updated Create Gauge Function
  const createGauges = (
    data: any[],
    targetElement: HTMLDivElement | null,
    fiscalDistressScore: string
  ) => {
    if (!targetElement || !Array.isArray(data)) return null;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;
    const el = d3.select(targetElement);

    // Clear Previous Renders
    el.selectAll("svg").remove();

    const margin = isMobile
    ? { top: 10, right: 10, bottom: 15, left: 10 }
    : { top: 20, right: 20, bottom: 25, left: 20 };
    const width = targetElement.offsetWidth - margin.left - margin.right
    const height = width / 2;
    const radius = width / 2;
    const barWidth = isMobile ? 25 : 40;
    const numberOfSections = 3;
    const padRadius = 0.02;
    const chartInset = isMobile ? 5 : 10;

    // Base offset to rotate the arc so the half circle sits flat
    const baseOffset = 0.75;
    const percentToDegree = (percent: number) => percent * 360;
    const degreeToRadius = (degree: number) => (degree * Math.PI) / 180;
    const percentToRadius = (percent: number) => degreeToRadius(percentToDegree(percent));

    // Determine actual gauge score percent from status data
    let currentStatus = fiscalDistressScore;
    let targetPercent = 0.16;

    // Define Arc Sections for Satisfactory, Cautionary, and Critical
    currentStatus === "Satisfactory" || currentStatus === "satisfactory" ? targetPercent = 0.84 : currentStatus === "Cautionary" || currentStatus === "cautionary" ? targetPercent = 0.50 : currentStatus === "Critical" ? targetPercent = 0.16 : targetPercent = 0.16;

    const targetDegrees = (targetPercent * 180) - 90;

    // Initialize SVG Elements
    const svg = el.append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)

    const chart = svg
      .append("g")
      .attr(
        "transform",
        `translate(${(width + margin.left) / 2}, ${height + margin.top})`
      );

    // Draw Background Arc Tracks
    const sectionPercent = 1 / numberOfSections / 2; 
    let accumulatedPercent = baseOffset;

    for (let index = 1; index <= numberOfSections; index++) {
      const arcStartRadius = percentToRadius(accumulatedPercent);
      const arcEndRadius = arcStartRadius + percentToRadius(sectionPercent);
      accumulatedPercent += sectionPercent;

      const startPad = index === 1 ? 0 : padRadius / 2;
      const endPad = index === numberOfSections ? 0 : padRadius / 2;

      const arcGenerator = d3.arc()
        .outerRadius(radius - chartInset)
        .innerRadius(radius - chartInset - barWidth)
        .startAngle(arcStartRadius - startPad)
        .endAngle(arcEndRadius - endPad);

      chart.append("path")
        .attr("class", `arc chart-color${index}`)
        .attr("d", arcGenerator as any);
    }

    // Trigger Needle Setup
    const needleLength = radius - chartInset - barWidth - (isMobile ? 5 : 10);
    const needleRadius = isMobile ? 5 : 8;

    // Create Needle Group
    const needleGroup = chart.append("g")
      .attr("class", "needle-group")
      .attr("transform", "rotate(-90)");

    // Append Needles to Circle
    needleGroup.append("circle")
      .attr("class", "needle-center")
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("r", needleRadius);

    // Append Needles to Path - M: Move To - L: Line To
    needleGroup.append("path")
      .attr("class", "needle")
      .attr("d", `M ${-needleRadius} 0 L 0 ${-needleLength} L ${needleRadius} 0 Z`);

    // Transition Needle Group
    needleGroup.transition()
      .delay(300)
      .duration(1500)
      .ease(d3.easeElasticOut.amplitude(1).period(0.6))
      .attr("transform", `rotate(${targetDegrees})`);

    return svg;
  };

  // Get All Entities
  useEffect(() => {
    getAllEntities();
  }, []);

  // Get FHI Data
  useEffect(() => {
    getEntityFhiData();
  }, [selectedEntityName]);

  // Updated Create Gauges
  useEffect(() => {
    if (!ugfBalance || ugfBalance.length === 0) return;

    // UGF Balance
    const currentUgfScore = getFiscalDistressScore(
      ugfBalance, 
      "ugf_balance_coverage_status", 
    );

    setUgfBalanceFDS(currentUgfScore);

    const ugfGaugeInstance = createGauges(
      ugfBalance, 
      ugfBalanceGaugeRef.current, 
      currentUgfScore
    );

    // Gov Fund Balance
    const currentGovFundBalanceScore = getFiscalDistressScore(
      govFundBalance,
      "gov_fund_coverage_status",
    );

    setGovFundBalanceFDS(currentGovFundBalanceScore);

    const govFundBalanceInstance = createGauges(
      govFundBalance,
      govFundBalanceGaugeRef.current,
      currentGovFundBalanceScore
    );

    // Gov Fund Revenue
    const currentGovFundRevenueScore = getFiscalDistressScore(
      govFundRevenue,
      "gov_fund_revenue_coverage_status",
    );

    setGovFundRevenueFDS(currentGovFundRevenueScore);

    const govFundRevenueInstance = createGauges(
      govFundRevenue,
      govFundRevenueGaugeRef.current,
      currentGovFundRevenueScore
    );

    // Cash Coverage of Gov Fund Expenditure
    const currentCashCoverageScore = getFiscalDistressScore(
      cashCoverageOfGovFundExpenditure,
      "cash_coverage_of_gov_fund_expenditure_status",
    );

    setCashCoverageFDS(currentCashCoverageScore)

    const cashCoverageOfGovFundExpenditureInstance = createGauges(
      cashCoverageOfGovFundExpenditure,
      cashCoverageGaugeRef.current,
      currentCashCoverageScore
    );

    // Debt Service
    const currentDebtServicesScore = getFiscalDistressScore(
      debtService,
      "debt_service_as_perc_of_gov_fund_status",
    );

    setDebtServiceFDS(currentDebtServicesScore);

    const debtServiceInstance = createGauges(
      debtService,
      debtServiceGaugeRef.current,
      currentDebtServicesScore
    );

    // Accumulated Depreciation
    const curentAccumulatedDepreciationScore = getFiscalDistressScore(
      accumulatedDepreciation,
      "accumulated_depreciation_of_depreciable_capital_assets_status",
    );

    setAccumulatedDepreciationFDS(curentAccumulatedDepreciationScore);

    const accumulatedDepreciationInstance = createGauges(
      accumulatedDepreciation,
      accumulatedDepreciationGaugeRef.current,
      curentAccumulatedDepreciationScore
    );

    // Clean up old D3 SVGs on Remount
    return () => {
      ugfGaugeInstance?.remove();
      govFundBalanceInstance?.remove();
      govFundRevenueInstance?.remove();
      cashCoverageOfGovFundExpenditureInstance?.remove();
      debtServiceInstance?.remove();
      accumulatedDepreciationInstance?.remove();
    }
  }, [ugfBalance, govFundBalance, govFundRevenue, cashCoverageOfGovFundExpenditure, debtService, accumulatedDepreciation]);

  // Create Tables
  useEffect(() => {
    if (!fhiData || fhiData.length === 0) return;

    const ugfBalanceTable = createTable(
      ugfBalance,
      ugfBalanceTableRef.current, 
      "report_year", 
      "ugf_balance_coverage", 
      "ugf_balance_coverage_status", 
      "ugf_balance_coverage_lag", 
      "ugf_balance_coverage_change"
    );

    getFiscalDistressScore(
      ugfBalance, 
      "ugf_balance_coverage_status", 
    );

    const govFundBalanceTable = createTable(
      govFundBalance,
      govFundBalanceTableRef.current, 
      "report_year", 
      "gov_fund_balance_coverage", 
      "gov_fund_coverage_status", 
      "gov_fund_coverage_lag", 
      "gov_fund_coverage_change"
    );

    getFiscalDistressScore(
      govFundBalance,
      "gov_fund_coverage_status",
    );

    const govFundRevenueTable = createTable( 
      govFundRevenue,
      govFundRevenueTableRef.current,
      "report_year", 
      "gov_fund_revenue_coverage", 
      "gov_fund_revenue_coverage_status", 
      "gov_fund_revenue_coverage_lag", 
      "gov_fund_revenue_coverage_change" 
    );
    
    getFiscalDistressScore(
      govFundRevenue,
      "gov_fund_coverage_status",
    );

    const cashCoverageOfGovFundExpenditureDataTable = createTable(
      cashCoverageOfGovFundExpenditure,
      cashCoverageOfGovFundExpenditureableRef.current, 
      "report_year", 
      "cash_coverage_of_gov_fund_expenditure", "cash_coverage_of_gov_fund_expenditure_status", "cash_coverage_of_gov_fund_expenditure_lag", "cash_coverage_of_gov_fund_expenditure_change"
    );

    getFiscalDistressScore(
      cashCoverageOfGovFundExpenditure,
      "cash_coverage_of_gov_fund_expenditure_status",
    );

    const debtServiceDataTable = createTable(
      debtService,
      debtServiceTableRef.current, 
      "report_year", 
      "debt_service_as_perc_of_gov_fund", 
      "debt_service_as_perc_of_gov_fund_status", "debt_service_as_perc_of_gov_fund_lag", "debt_service_as_perc_of_gov_fund_change"
    );

    getFiscalDistressScore(
      debtService,
      "debt_service_as_perc_of_gov_fund_status",
    );

    const accumulatedDepreciationDataTable = createTable(
      accumulatedDepreciation,
      accumulatedDepreciationTableRef.current,
      "report_year",
      "accumulated_depreciation_of_depreciable_capital_assets",
      "accumulated_depreciation_of_depreciable_capital_assets_status",
      "accumulated_depreciation_of_depreciable_capital_assets_lag",
      "accumulated_depreciation_of_depreciable_capital_assets_change",
    );
    
    getFiscalDistressScore(
      accumulatedDepreciation,
      "accumulated_depreciation_of_depreciable_capital_assets_status",
    );

    // Added to prevent overlapping/duplicate tables when a new entity is selected
    return () => {
      ugfBalanceTable?.destroy();
      govFundBalanceTable?.destroy();
      govFundRevenueTable?.destroy();
      cashCoverageOfGovFundExpenditureDataTable?.destroy();
      debtServiceDataTable.destroy();
      accumulatedDepreciationDataTable.destroy();
    };
  }, [fhiData]); 
    
  return (
    <section>
      <Row className="mb-3">
        {loading ? <Loader /> : <></>}

        {/* Select an Entity / Gauges Container Card */} 
        <Card className="px-0 mb-4 shadow-sm"> 
          <Card.Header className="bg-secondary text-white border-0 py-3"> 
            <h3 className="m-0 text-white">Select an Entity</h3> 
          </Card.Header> 
          
          <Card.Body className="p-4"> 
            <Row className="mb-4">
              <Col xs={12} xl={4}>
                <Card className="border-0 bg-light"> 
                  <Card.Body className="p-3"> 
                    <h5 className="d-flex align-items-center gap-2 mb-3"> 
                      <i className="fas fa-landmark text-secondary"/> Select an Entity </h5> 
                    <SingleSelectAsync 
                      value={selectedEntityName ? { label: selectedEntityName, value: selectedEntityName } : null} 
                      onChange={(newValue) => setSelectedEntityName(newValue ? newValue.value : null)} 
                      loadOptions={loadEntityOptions} 
                      defaultOptions={memoizedEntityNameOptions.length > 0 ? memoizedEntityNameOptions : true} 
                      type="entity" 
                    /> 
                  </Card.Body> 
                </Card>
              </Col>
            </Row>

            <Row className="row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
              
              {/* Gauge 1: UGF Balance */}
              <Col>
                {/* Updated from selectedEntityName to allGaugesLoaded */}
                {selectedEntityName ?
                  <Card className="shadow-sm border-light h-100"> 
                    <Card.Body className="p-3 d-flex flex-column align-items-center justify-content-between"> 
                      <h6 className="text-muted fw-bold text-uppercase text-center w-100 mb-3">UGF Balance</h6> 
                      <div ref={ugfBalanceGaugeRef} style={{ width: "100%", maxWidth: "300px", 
                        minHeight: "200px", 
                        display: "flex", justifyContent: "center" }} />
                      <a href="#ugf-balance" className="mt-3 ">Explanation</a> 
                    </Card.Body> 
                  </Card>
                  : <></>
                }
              </Col>

              {/* Gauge 2: Gov Fund Balance */}
              <Col>
                {/* Updated from selectedEntityName to allGaugesLoaded */}
                {selectedEntityName ?
                  <Card className="shadow-sm border-light h-100"> 
                    <Card.Body className="p-3 d-flex flex-column align-items-center justify-content-between"> 
                      <h6 className="text-muted fw-bold text-uppercase text-center w-100 mb-3">Gov Fund Balance</h6> 
                      <div ref={govFundBalanceGaugeRef} style={{ width: "100%", maxWidth: "300px", minHeight: "200px", display: "flex", justifyContent: "center" }} />
                      <a href="#gov-fund-balance" className="mt-3 ">Explanation</a>
                    </Card.Body> 
                  </Card>
                  : <></>
                }
              </Col>

              {/* Gauge 3: Gov Fund Revenue */}
              <Col>
                {selectedEntityName ?
                <Card className="shadow-sm border-light h-100"> 
                  <Card.Body className="p-3 d-flex flex-column align-items-center justify-content-between"> 
                    <h6 className="text-muted fw-bold text-uppercase text-center w-100 mb-3">Gov Fund Revenue</h6> 
                    <div ref={govFundRevenueGaugeRef} style={{ width: "100%", maxWidth: "300px", minHeight: "200px", display: "flex", justifyContent: "center" }} />
                    <a href="#gov-fund-revenue" className="mt-3 ">Explanation</a> 
                  </Card.Body> 
                </Card>
                : <></>
                }
              </Col>

              {/* Gauge 4: Cash Coverage */}
              <Col>
                {selectedEntityName ?
                  <Card className="shadow-sm border-light h-100"> 
                    <Card.Body className="p-3 d-flex flex-column align-items-center justify-content-between"> 
                      <h6 className="text-muted fw-bold text-uppercase text-center w-100 mb-3">Cash Coverage</h6> 
                      <div ref={cashCoverageGaugeRef} style={{ width: "100%", maxWidth: "300px", minHeight: "200px", display: "flex", justifyContent: "center" }} />
                      <a href="#cash-coverage" className="mt-3 ">Explanation</a> 
                    </Card.Body> 
                  </Card>
                  : <></>
                }
              </Col>

              {/* Gauge 5: Debt Service */}
              <Col>
                {selectedEntityName ?
                  <Card className="shadow-sm border-light h-100"> 
                    <Card.Body className="p-3 d-flex flex-column align-items-center justify-content-between"> 
                      <h6 className="text-muted fw-bold text-uppercase text-center w-100 mb-3">Debt Service %</h6> 
                      <div ref={debtServiceGaugeRef} style={{ width: "100%", maxWidth: "300px", minHeight: "200px", display: "flex", justifyContent: "center" }} />
                      <a href="#debt-service" className="mt-3 ">Explanation</a> 
                    </Card.Body> 
                  </Card>
                  : <></>
                }
              </Col>

              {/* Gauge 6: Accumulated Depreciation */}
              <Col>
                {selectedEntityName ?
                  <Card className="shadow-sm border-light h-100"> 
                    <Card.Body className="p-3 d-flex flex-column align-items-center justify-content-between"> 
                      <h6 className="text-muted fw-bold text-uppercase text-center w-100 mb-3">Accumulated Depr. Score</h6> 
                      <div ref={accumulatedDepreciationGaugeRef} style={{ width: "100%", maxWidth: "300px", minHeight: "200px", display: "flex", justifyContent: "center" }} />
                      <a href="#accumulated-depreciation"  className="mt-3 ">Explanation</a> 
                    </Card.Body> 
                  </Card>
                  : <></>
                }
              </Col>
            </Row>
          </Card.Body> 
        </Card>

        {/* UGF Balance */}
        <Card className="px-0 mb-4" id="ugf-balance">
          <Card.Header 
            className={
              ugfBalanceFDS === "Satisfactory" || ugfBalanceFDS === "" 
                ? "bg-secondary text-white" 
                : ugfBalanceFDS === "Cautionary" 
                ? "bg-primary text-white" 
                : "bg-danger text-white"
            }
            style={{ border: "none" }}
          >
            <h3 className="text-white">UGF Balance</h3>
          </Card.Header>
          <Card.Body className="p-2 px-3">
            <Row>
              <Col xs={12} sm={12} md={12} lg={12} xl={12} className="my-3">
                <h4 className="text-uppercase mb-1 h5">Formula</h4>
                <p>Governmental Fund Balance/Governmental Fund Expenditures</p>
                
                <h4 className="text-uppercase mb-1 h5">Purpose</h4>
                <p>
                  Measures flexibility available for unexpected expenses. Examines fund balance versus expenditures, in governmental funds.
                </p>
                
                <h4 className="text-uppercase mb-1 h5">Score System</h4>
                <ul>
                  <li className="fw-bolder">
                    Satisfactory: 2 or 3 of the last 3 years are greater than the 25% benchmark.
                  </li>
                  <li>
                    Cautionary: 1 of the last 3 years are greater than the 25% benchmark.
                  </li>
                  <li>
                    Critical: 0 of the last 3 years are greater than the 25% benchmark
                  </li>
                </ul>
                
                <h4 className="text-uppercase mb-1 h5">Data</h4>
                
                <div 
                  ref={ugfBalanceTableRef} 
                  className="table-light" 
                  style={{ opacity: 1, height: "auto", overflow: "hidden", marginTop: "15px" }}
                >
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Gov Fund Balance Coverage */}
        <Card className="px-0 mb-4" id="gov-fund-balance">
          <Card.Header
            className={
              govFundBalanceFDS === "Satisfactory" || govFundBalanceFDS === ""
                ? "bg-secondary text-white" 
                : govFundBalanceFDS === "Cautionary" 
                ? "bg-primary text-white" 
                : "bg-danger text-white"
            }
            style={{ border: "none" }} 
          >
            <h3 className="text-white">Gov Fund Balance Coverage</h3>
          </Card.Header>
          <Card.Body className="p-2 px-3">
            <Row>
              <Col xs={12} sm={12} md={12} lg={12} xl={12} className="my-3">
                <h4 className="text-uppercase mb-1 h5">Formula</h4>
                <p>
                  Governmental Fund Balance/Governmental Fund Expenditures
                </p>

                <h4 className="text-uppercase mb-1 h5">Purpose</h4>
                <p>
                  Measures flexibility available for unexpected expenses. Examines fund balance versus expenditures, in governmental funds.
                </p>

                <h4 className="text-uppercase mb-1 h5">Score System</h4>
                <ul>
                  <li className="fw-bolder">
                    Satisfactory: 2 or 3 of the last 3 years are greater than the 25% benchmark.
                  </li>
                  <li>
                    Cautionary: 1 of the last 3 years are greater than the 25% benchmark.
                  </li>
                  <li>
                    Critical: 0 of the last 3 years are greater than the 25% benchmark
                  </li>
                </ul>

                <h4 className="text-uppercase mb-1 h5">Data</h4>

                <div 
                  ref={govFundBalanceTableRef} 
                  className="table-light" 
                  style={{ opacity: 1, height: "auto", overflow: "hidden", marginTop: "15px" }}
                ></div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Gov Fund Revenue Coverage */}
        <Card className="px-0 mb-4" id="gov-fund-revenue">
          <Card.Header
            className={
              govFundRevenueFDS === "Satisfactory" || govFundRevenueFDS === ""
                ? "bg-secondary text-white" 
                : govFundRevenueFDS === "Cautionary" 
                ? "bg-primary text-white" 
                : "bg-danger text-white"
            }
            style={{ border: "none" }}  
          >
            <h3 className="text-white">Gov Fund Revenue Coverage</h3>
          </Card.Header>
          <Card.Body className="p-2 px-3">
            <Row>
              <Col xs={12} sm={12} md={12} lg={12} xl={12} className="my-3">
                <h4 className="text-uppercase mb-1 h5">Formula</h4>
                <p>
                  Governmental Fund Revenues/Governmental Fund Expenditures
                </p>

                <h4 className="text-uppercase mb-1 h5">Purpose</h4>
                <p>
                  Measures revenues versus expenditures. Examines incoming governmental revenues versus outgoing governmental expenditures.
                </p>

                <h4 className="text-uppercase mb-1 h5">Score System</h4>
                <ul>
                  <li className="fw-bolder">
                    Satisfactory: 2 or 3 of the last 3 years are greater than the 100% benchmark.
                  </li>
                  <li>
                    Cautionary: 1 of the last 3 years are greater than the 100% benchmark.
                  </li>
                  <li>
                    Critical: 0 of the last 3 years are greater than the 100% benchmark
                  </li>
                </ul>

                <h4 className="text-uppercase mb-1 h5">Data</h4>

                <div 
                  ref={govFundRevenueTableRef} 
                  className="table-light" 
                  style={{ opacity: 1, height: "auto", overflow: "hidden", marginTop: "15px" }}
                ></div>
              </Col>
            </Row>
          </Card.Body>
        </Card>


        {/* Cash Coverage */}
        <Card className="px-0 mb-4" id="cash-coverage">
          <Card.Header 
            className={
              cashCoverageFDS === "Satisfactory" || cashCoverageFDS === ""
                ? "bg-secondary text-white" 
                : cashCoverageFDS === "Cautionary" 
                ? "bg-primary text-white" 
                : "bg-danger text-white"
            }
            style={{ border: "none" }}
          >
            <h3 className="text-white">Cash Coverage</h3>
          </Card.Header>
          <Card.Body className="p-2 px-3">
            <Row>
              <Col xs={12} sm={12} md={12} lg={12} xl={12} className="my-3">
                <h4 className="text-uppercase mb-1 h5">Formula</h4>
                <p>
                  Cash & Cash Equivalents/Governmental Fund Expenditures
                </p>

                <h4 className="text-uppercase mb-1 h5">Purpose</h4>
                <p>
                  Measures liquidity, or how long the entity could operate and maintain service levels if all accounts payable immediately came
                </p>

                <h4 className="text-uppercase mb-1 h5">Score System</h4>
                <ul>
                  <li className="fw-bolder">
                    Satisfactory: 2 or 3 of the last 3 years are greater than the 35% benchmark.
                  </li>
                  <li>
                    Cautionary: 1 of the last 3 years are greater than the 35% benchmark.
                  </li>
                  <li>
                    Critical: 0 of the last 3 years are greater than the 35% benchmark.
                  </li>
                </ul>

                <h4 className="text-uppercase mb-1 h5">Data</h4>
                <div 
                  ref={cashCoverageOfGovFundExpenditureableRef} 
                  className="table-light" 
                  style={{ opacity: 1, height: "auto", overflow: "hidden", marginTop: "15px" }}
                ></div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Debt Service % */}
        <Card className="px-0 mb-4" id="debt-service">
          <Card.Header 
            className={
              debtServiceFDS === "Satisfactory" || debtServiceFDS === ""
                ? "bg-secondary text-white" 
                : debtServiceFDS === "Cautionary" 
                ? "bg-primary text-white" 
                : "bg-danger text-white"
            }
            style={{ border: "none" }} 
            >
            <h3 className="text-white">Debt Service %</h3>
          </Card.Header>
          <Card.Body className="p-2 px-3">
            <Row>
              <Col xs={12} sm={12} md={12} lg={12} xl={12} className="my-3">
                <h4 className="text-uppercase mb-1 h5">Formula</h4>
                <p>
                  Debt Service/ Governmental Fund Expenditures
                </p>

                <h4 className="text-uppercase mb-1 h5">Purpose</h4>
                <p>
                  Measures how burdened entity is by debt. Debts have a legal obligation to be paid. More debt means less expenditures allocated to maintaining service levels and capital assets. Examines proportion of debt payments (principle and interest) to General fund expenditures.
                </p>

                <h4 className="text-uppercase mb-1 h5">Score System</h4>
                <ul>
                  <li className="fw-bolder">
                    Satisfactory: 2 or 3 of the last 3 years are less than the 25% benchmark.
                  </li>
                  <li>
                    Cautionary: 1 of the last 3 years are less than the 25% benchmark.
                  </li>
                  <li>
                    Critical: 0 of the last 3 years are less than the 25% benchmark.
                  </li>
                </ul>

                <h4 className="text-uppercase mb-1 h5">Data</h4>

                <div 
                  ref={debtServiceTableRef} 
                  className="table-light" 
                  style={{ opacity: 1, height: "auto", overflow: "hidden", marginTop: "15px" }}
                ></div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Accumulated Depr. Score */}
        <Card className="px-0 mb-4" id="accumulated-depreciation">
          <Card.Header
            className={
              accumulatedDepreciationFDS === "Satisfactory" || accumulatedDepreciationFDS === ""
                ? "bg-secondary text-white" 
                : accumulatedDepreciationFDS === "Cautionary" 
                ? "bg-primary text-white" 
                : "bg-danger text-white"
            } 
            >
            <h3 className="text-white">Accumulated Depr. Score</h3>
          </Card.Header>
          <Card.Body className="p-2 px-3">
            <Row>
              <Col xs={12} sm={12} md={12} lg={12} xl={12} className="my-3">
                <h4 className="text-uppercase mb-1 h5">Formula</h4>
                <p>
                  Accumulated Depreciation/Depreciable Capital Assets
                </p>

                <h4 className="text-uppercase mb-1 h5">Purpose</h4>
                <p>
                  Measures the extent an entity is maintaining its capital assets (e.g. roads, pipes, building, etc.). If capital assets maintenance is delayed, increasing depreciation will produce lower service levels and eventual asset failure, resulting in increasing repair costs and imminent high replacement costs.
                </p>

                <h4 className="text-uppercase mb-1 h5">Score System</h4>
                <ul>
                  <li>
                    Satisfactory: 2 or 3 of the last 3 years are less than the 70% benchmark.
                  </li>
                  <li className="fw-bolder">
                    Cautionary: 1 of the last 3 years are less than the 70% benchmark.
                  </li>
                  <li>
                    Critical: 0 of the last 3 years are less than the 70% benchmark.
                  </li>
                </ul>

                <h4 className="text-uppercase mb-1 h5">Data</h4>

                <div 
                  ref={accumulatedDepreciationTableRef} 
                  className="table-light" 
                  style={{ opacity: 1, height: "auto", overflow: "hidden", marginTop: "15px" }}
                ></div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Row>
    </section>
  )
}

export default EntityFiscalDistressIndividualReports;